import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { saveAs } from 'file-saver';
import api from '../services/api';
import QuoteForm from '../components/QuoteForm';
import { getDisplayName } from '../utils/i18n';
import {
  ArrowLeftIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

// ---------------------------------------------------------------------------
// Label maps for raw enum values
// ---------------------------------------------------------------------------
const QUANTITY_LABELS = {
  '1_5': '1 – 5',
  '6_20': '6 – 20',
  '21_50': '21 – 50',
  '51_100': '51 – 100',
  '101_1000': '101 – 1000',
  '1000+': '1000+',
  NOT_SURE: 'Not sure',
};

const TIMELINE_LABELS = {
  ASAP: 'ASAP',
  '1W': '1 Week',
  '2W': '2 Weeks',
  '1M': '1 Month',
  FLEXIBLE: 'Flexible',
};

const STATUS_COLORS = {
  CREATED: 'bg-slate-100 text-slate-700',
  BUILT: 'bg-slate-100 text-slate-700',
  SLICED: 'bg-slate-100 text-slate-700',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-emerald-100 text-emerald-800',
  ONGOING: 'bg-indigo-100 text-indigo-800',
  INSPECTION: 'bg-orange-100 text-orange-800',
  READY: 'bg-teal-100 text-teal-800',
  DELIVERING: 'bg-cyan-100 text-cyan-800',
  DONE: 'bg-green-200 text-green-800',
  ON_HOLD: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-slate-200 text-slate-700',
  REJECTED: 'bg-red-200 text-red-800',
  ABANDONED: 'bg-slate-200 text-slate-600',
  REFUNDED: 'bg-purple-100 text-purple-800',
  EXPIRED: 'bg-slate-200 text-slate-500',
};

// ---------------------------------------------------------------------------
// Helper: format bytes to a readable size string
// ---------------------------------------------------------------------------
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// ---------------------------------------------------------------------------
// Helper: format a status key to a readable label
// ---------------------------------------------------------------------------
const formatStatus = (status) => {
  if (!status) return '';
  return status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

// ---------------------------------------------------------------------------
// Helper: full datetime string
// ---------------------------------------------------------------------------
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------------------------------------------------------------------------
// Sub-component: a labelled field in the detail grid
// ---------------------------------------------------------------------------
function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {label}
      </dt>
      <dd className="text-sm text-slate-900">{children || '—'}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: section card wrapper
// ---------------------------------------------------------------------------
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
        {Icon && <Icon className="h-4 w-4" />}
        {title}
      </h2>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export default function QuoteDetail() {
  const { id } = useParams();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/auth/quotes/${id}`);
      // The backend wraps in { success, data } for list but returns the document
      // directly for single-item (responseSuccessHandler with a plain object).
      // Handle both shapes defensively.
      const payload = response.data;
      setQuote(payload?.data ?? payload);
    } catch (err) {
      console.error('Failed to load quote:', err);
      setError('Failed to load quote. It may have been deleted or you may not have access.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const handleSave = async (formData) => {
    await api.patch(`/auth/quotes/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setShowEditForm(false);
    await fetchQuote();
  };

  // Download a quote attachment through the authenticated backend endpoint.
  // The backend streams the S3 object — never exposes a raw presigned URL to the browser.
  // Route: GET /auth/quotes/download/:quoteId/:fileIndex
  const handleDownloadFile = async (fileIndex, fileName) => {
    try {
      const response = await api.get(
        `/auth/quotes/download/${quote._id}/${fileIndex}`,
        { responseType: 'blob' },
      );
      saveAs(response.data, fileName);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  // ------------------------------------------------------------------
  // Loading / error states
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="text-sm text-slate-500">Loading quote…</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="space-y-4">
        <Link
          to="/quotes"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Quotes
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
          {error || 'Quote not found.'}
        </div>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[quote.status] ?? 'bg-slate-100 text-slate-700';

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                           */}
      {/* ---------------------------------------------------------------- */}
      <div>
        <Link
          to="/quotes"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Quotes
        </Link>

        <PageHeader
          title={`Quote from ${quote.contactName || '—'}`}
          description={`ID: ${quote._id}`}
          actions={
            <div className="flex items-center gap-2">
              {quote.status && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor}`}>
                  {formatStatus(quote.status)}
                </span>
              )}
              <button
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </button>
            </div>
          }
        />
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Two-column layout                                                */}
      {/* ---------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ---- Left column ---- */}
        <div className="lg:col-span-1 space-y-5">

          {/* Contact Information */}
          <Section title="Contact" icon={UserIcon}>
            <dl className="space-y-3">
              <Field label="Name">{quote.contactName}</Field>
              <Field label="Email">
                {quote.contactEmail ? (
                  <a
                    href={`mailto:${quote.contactEmail}`}
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <EnvelopeIcon className="h-3.5 w-3.5" />
                    {quote.contactEmail}
                  </a>
                ) : null}
              </Field>
              {quote.phone && (
                <Field label="Phone">
                  <a
                    href={`tel:${quote.phone}`}
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <PhoneIcon className="h-3.5 w-3.5" />
                    {quote.phone}
                  </a>
                </Field>
              )}
              {quote.company && (
                <Field label="Company">
                  <span className="inline-flex items-center gap-1">
                    <BuildingOfficeIcon className="h-3.5 w-3.5 text-slate-400" />
                    {quote.company}
                  </span>
                </Field>
              )}
            </dl>
          </Section>

          {/* Metadata */}
          <Section title="Metadata">
            <dl className="space-y-3">
              <Field label="Status">
                {quote.status && (
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor}`}>
                    {formatStatus(quote.status)}
                  </span>
                )}
              </Field>
              <Field label="Quote ID">
                <span className="font-mono text-xs text-slate-600 break-all">{quote._id}</span>
              </Field>
              {quote.quoteId && (
                <Field label="Reference">
                  <span className="font-mono text-xs text-slate-600">{quote.quoteId}</span>
                </Field>
              )}
              <Field label="Submitted">{formatDate(quote.createdAt)}</Field>
              <Field label="Last updated">{formatDate(quote.updatedAt)}</Field>
            </dl>
          </Section>

          {/* Internal Notes */}
          {quote.notes !== undefined && (
            <Section title="Internal Notes">
              {quote.notes ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{quote.notes}</p>
              ) : (
                <p className="text-sm text-slate-400 italic">No internal notes</p>
              )}
            </Section>
          )}
        </div>

        {/* ---- Right column ---- */}
        <div className="lg:col-span-2 space-y-5">

          {/* Service Details */}
          <Section title="Service Details" icon={DocumentTextIcon}>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Quantity">
                {QUANTITY_LABELS[quote.quantity] ?? quote.quantity ?? '—'}
              </Field>
              <Field label="Timeline">
                {TIMELINE_LABELS[quote.timeline] ?? quote.timeline ?? '—'}
              </Field>
              {quote.quality && (
                <Field label="Quality (layer height)">
                  {quote.quality === 'NOT_SURE' ? 'Not sure' : `${quote.quality} mm`}
                </Field>
              )}
              {(quote.infill !== undefined && quote.infill !== '') && (
                <Field label="Infill">{quote.infill}%</Field>
              )}
            </dl>
          </Section>

          {/* Material */}
          {(quote.material || (quote.materialExtras && quote.materialExtras.length > 0) || quote.color) && (
            <Section title="Material & Colour">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                {quote.material && (
                  <Field label="Material">
                    {getDisplayName(
                      typeof quote.material === 'object' ? quote.material.name : quote.material
                    )}
                  </Field>
                )}
                {quote.materialExtras && quote.materialExtras.length > 0 && (
                  <Field label="Material Extras">
                    {quote.materialExtras
                      .map((me) =>
                        getDisplayName(typeof me === 'object' ? me.name : me)
                      )
                      .filter(Boolean)
                      .join(', ')}
                  </Field>
                )}
                {quote.color && (quote.color.name || quote.color.hex) && (
                  <Field label="Colour">
                    <span className="inline-flex items-center gap-2">
                      {quote.color.hex && (
                        <span
                          className="inline-block w-5 h-5 rounded border border-slate-300 shadow-sm flex-shrink-0"
                          style={{ backgroundColor: `#${quote.color.hex}` }}
                        />
                      )}
                      <span>{quote.color.name || ''}</span>
                      {quote.color.hex && (
                        <span className="text-xs text-slate-400 font-mono">
                          #{quote.color.hex}
                        </span>
                      )}
                    </span>
                  </Field>
                )}
              </dl>
            </Section>
          )}

          {/* Description */}
          {quote.description && (
            <Section title="Description">
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {quote.description}
              </p>
            </Section>
          )}

          {/* Attachments */}
          {quote.files && quote.files.length > 0 && (
            <Section title={`Attachments (${quote.files.length})`} icon={PaperClipIcon}>
              <ul className="space-y-2">
                {quote.files.map((file, idx) => (
                  <li key={idx}>
                    <button
                      type="button"
                      onClick={() => handleDownloadFile(idx, file.originalName)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors group text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <PaperClipIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {file.originalName}
                          </p>
                          <p className="text-xs text-slate-500">
                            {[
                              file.mimeType,
                              formatFileSize(file.fileSize),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                      </div>
                      <ArrowDownTrayIcon className="h-4 w-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0 transition-colors" />
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Edit modal (inline — does not navigate away)                     */}
      {/* ---------------------------------------------------------------- */}
      {showEditForm && (
        <QuoteForm
          quote={quote}
          onSave={handleSave}
          onCancel={() => setShowEditForm(false)}
        />
      )}
    </div>
  );
}
